import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface PaginationProps {
  page: number;          // 1-based current page
  totalElements: number;
  pageSize: number;
  onChange: (page: number) => void;
  className?: string;
  /** Opt-in "per page" picker. Both must be passed for it to render. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

/**
 * A page size the viewer picked, remembered per page in localStorage.
 *
 * Browser storage can be missing or throw (private windows, blocked site
 * data), so every access is guarded and a stored value outside `options`
 * falls back to the default.
 */
export function usePageSize(storageKey: string, options: number[], fallback: number) {
  const [size, setSize] = useState<number>(() => {
    try {
      const stored = Number(localStorage.getItem(storageKey));
      if (options.includes(stored)) return stored;
    } catch { /* storage unavailable */ }
    return fallback;
  });
  const update = (next: number) => {
    setSize(next);
    try { localStorage.setItem(storageKey, String(next)); } catch { /* storage unavailable */ }
  };
  return [size, update] as const;
}

export function Pagination({
  page,
  totalElements,
  pageSize,
  onChange,
  className,
  pageSizeOptions,
  onPageSizeChange,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalElements);
  const showSizePicker = !!pageSizeOptions?.length && !!onPageSizeChange;

  // With a picker, stay visible while a smaller page size would still split
  // the list — otherwise a viewer on 48/page could never get back to 12.
  if (totalPages <= 1 && !(showSizePicker && totalElements > Math.min(...pageSizeOptions!))) return null;

  const pageNumbers: (number | "…")[] = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | "…")[] = [];
    if (page <= 4) {
      pages.push(1, 2, 3, 4, 5, "…", totalPages);
    } else if (page >= totalPages - 3) {
      pages.push(1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "…", page - 1, page, page + 1, "…", totalPages);
    }
    return pages;
  })();

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3 border-t border-border/50 pt-4", className)}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs text-muted-foreground">
          Showing{" "}
          <span className="font-medium text-foreground">{start.toLocaleString()}–{end.toLocaleString()}</span>{" "}
          of{" "}
          <span className="font-medium text-foreground">{totalElements.toLocaleString()}</span>
        </p>
        {showSizePicker && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Per page
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange!(Number(e.target.value))}
              className="h-8 rounded-md border border-border/70 bg-background px-2 text-xs font-medium text-foreground outline-none transition-colors hover:border-primary/40 focus:border-primary/60"
            >
              {pageSizeOptions!.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-border/70 text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-35"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {pageNumbers.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-0.5 text-xs text-muted-foreground">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-md border text-xs font-medium transition-colors",
                p === page
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border/70 text-muted-foreground hover:border-primary/40 hover:bg-muted hover:text-foreground"
              )}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-border/70 text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-35"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
